import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { FadeUp, SectionHeading } from "@/components/anim";
import { CONTACT, PRIVATE_LESSONS, FALLBACK_IMAGE, SITE_URL } from "@/lib/site";

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Private Dance Lessons",
  serviceType: "One-to-one dance tuition",
  provider: { "@type": "LocalBusiness", name: "Kerry's", areaServed: "Whitstable" },
  areaServed: "Whitstable, Kent",
  url: `${SITE_URL}/private-lessons`,
  offers: {
    "@type": "Offer",
    price: "35",
    priceCurrency: "GBP",
    description: "£35 per hour including a local Whitstable venue (Treetops Dance Studio)",
  },
};

const USE_CASES = [
  { n: "01", title: "ISTD exam polish", text: "One-to-one time on the exact exercises an examiner wants to see — the fastest way to turn a Pass into a Distinction." },
  { n: "02", title: "Festival & competition solos", text: "Choreography built for your dancer, rehearsed until it feels like second nature." },
  { n: "03", title: "Vocational audition prep", text: "Applying for performing arts colleges? Kerry has been through those doors herself — and coached plenty of students through them since." },
  { n: "04", title: "Catching up or getting ahead", text: "Missed a term? Joining a new grade? A few privates close the gap quickly and kindly." },
  { n: "05", title: "Confidence building", text: "Some children simply do better one-to-one before they join a group. That's fine — we start where your child is." },
];

export default function PrivateLessons() {
  return (
    <>
      <Seo
        title="Private Dance Lessons"
        description={"One-to-one private dance lessons in Whitstable with Kerry — all ages, all dance styles, £35 per hour including a local venue. Wednesdays and Fridays at Treetops Dance Studio."}
        path="/private-lessons"
        jsonLd={SERVICE_JSONLD}
      />
      <section className="grain border-b-[0.5px] border-foreground/30 overflow-hidden" data-testid="privates-hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <FadeUp>
              <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-1.5 mb-6">
                All ages &middot; all dance styles &middot; venue included
              </Badge>
              <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
                Private
                <br />
                <span className="text-[hsl(var(--kerrys-sea))]">lessons.</span>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                One dancer, one teacher, one goal. Whether it&rsquo;s exam polish, a festival solo, audition prep or pure
                confidence building &mdash; an hour of Kerry&rsquo;s full attention goes a very long way.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="privates-phone-button"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="h-4 w-4 mr-2" /> Call {CONTACT.phone}
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="privates-email-button"
                >
                  <a href={CONTACT.emailHref}>
                    <Mail className="h-4 w-4 mr-2" /> Email Kerry
                  </a>
                </Button>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-5 relative lg:-mr-10">
            <FadeUp delay={0.12}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1531301147855-9f3e913bfe58?w=900&q=80"
                  alt="Dancer working one-to-one in the studio"
                  className="w-full h-[340px] sm:h-[440px] lg:h-[540px] object-cover border-[0.5px] border-foreground"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }}
                />
                <div
                  className="absolute -bottom-7 -left-4 sm:-left-8 bg-[hsl(var(--kerrys-sun))] text-foreground px-6 py-4 -rotate-2 border-[0.5px] border-foreground"
                  data-testid="privates-price-stamp"
                >
                  <p className="font-display text-5xl leading-none">{PRIVATE_LESSONS.price}<span className="text-2xl">/hr</span></p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] mt-1.5 text-foreground/70">Venue included</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
          <FadeUp className="sm:col-span-5">
            <div className="border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] grain p-8 sm:p-10 h-full" data-testid="privates-fact-tile">
              <p className="font-display text-6xl sm:text-7xl leading-none">{PRIVATE_LESSONS.price}</p>
              <p className="text-sm font-bold uppercase tracking-[0.2em] mt-4">Per hour, all in</p>
              <p className="text-sm text-foreground/70 mt-2 max-w-xs">
                That includes hire of a local Whitstable venue &mdash; no hidden extras, no studio fee on top.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.08} className="sm:col-span-4 lg:translate-y-8">
            <div className="border-[0.5px] border-foreground bg-card p-8 sm:p-10 h-full" data-testid="privates-fact-tile">
              <Calendar className="h-8 w-8 text-[hsl(var(--kerrys-sea))]" />
              <p className="font-display uppercase text-3xl sm:text-4xl leading-[0.95] mt-4">{PRIVATE_LESSONS.days}</p>
              <p className="text-sm text-muted-foreground mt-2">At {PRIVATE_LESSONS.venue}, Whitstable. Times arranged directly with Kerry.</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.16} className="sm:col-span-3 lg:translate-y-16">
            <div className="border-[0.5px] border-foreground bg-card p-8 sm:p-10 h-full" data-testid="privates-fact-tile">
              <MapPin className="h-8 w-8 text-[hsl(var(--kerrys-coral))]" />
              <p className="font-display uppercase text-3xl sm:text-4xl leading-[0.95] mt-4">Any age, any style</p>
              <p className="text-sm text-muted-foreground mt-2">Ballet, tap, modern, street, musical theatre &mdash; from tiny dancers to adults.</p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-secondary border-y-[0.5px] border-foreground/40 grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
          <SectionHeading eyebrow="Why book one" title="What privates are brilliant for" />
          <div className="border-t-[0.5px] border-foreground mt-14">
            {USE_CASES.map((u, i) => (
              <FadeUp key={u.n} delay={i * 0.03}>
                <div className="grid grid-cols-12 gap-4 items-start py-7 border-b-[0.5px] border-foreground px-2" data-testid="privates-usecase-row">
                  <p className="font-display text-xl sm:text-2xl text-[hsl(var(--kerrys-coral))] col-span-2 sm:col-span-1">{u.n}</p>
                  <p className="font-display uppercase text-2xl sm:text-4xl leading-[0.95] col-span-10 sm:col-span-5">{u.title}</p>
                  <p className="col-span-12 sm:col-span-6 text-sm sm:text-base text-muted-foreground leading-relaxed">{u.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
        <SectionHeading eyebrow="How it works" title="Three steps, no faff" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {[
            { n: "1", title: "Get in touch", text: "Ring or email Kerry with your dancer's age, style and what you'd like to work on." },
            { n: "2", title: "Pick a slot", text: "Privates run Wednesdays and Fridays at Treetops Dance Studio — you'll agree a time that suits." },
            { n: "3", title: "Dance", text: "A full hour, one-to-one, £35 all in. Book a one-off or a regular weekly slot." },
          ].map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.07} className={i === 1 ? "md:translate-y-8" : ""}>
              <div className="border-[0.5px] border-foreground bg-card p-8 h-full" data-testid="privates-step">
                <p className="font-display text-5xl text-[hsl(var(--kerrys-sea))]">{s.n}</p>
                <h3 className="font-display uppercase text-3xl leading-[0.95] mt-3">{s.title}</h3>
                <p className="text-muted-foreground mt-3 leading-relaxed">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="bg-[hsl(var(--kerrys-coral))] border-t-[0.5px] border-foreground grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
          <FadeUp>
            <h2 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.9] text-foreground">
              An hour that&rsquo;s
              <br />
              all theirs.
            </h2>
            <p className="mt-6 text-foreground/80 text-lg max-w-xl leading-relaxed">
              {PRIVATE_LESSONS.price} {PRIVATE_LESSONS.per}, venue included, arranged directly with Kerry.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                data-testid="privates-cta-phone-button"
              >
                <a href={CONTACT.phoneHref}>
                  <Phone className="h-4 w-4 mr-2" /> {CONTACT.phone}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                data-testid="privates-cta-email-button"
              >
                <a href={CONTACT.emailHref}>Email Kerry <ArrowRight className="h-4 w-4 ml-2" /></a>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
